import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  FolderSearch,
  History,
  MessageSquareWarning,
  ShieldCheck,
  Tag,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';

type CqcDomain = 'Safe' | 'Effective' | 'Caring' | 'Responsive' | 'Well-led';
type EvidenceStatus = 'current' | 'review_due' | 'gap';
type ComplaintStatus = 'open' | 'investigating' | 'resolved' | 'overdue';

interface DomainScore {
  domain: CqcDomain;
  score: number;
  rating: 'Outstanding' | 'Good' | 'Requires improvement';
  evidenceCount: number;
  gaps: number;
}

interface EvidenceItem {
  id: string;
  title: string;
  domain: CqcDomain;
  qualityStatement: string;
  category: string;
  owner: string;
  status: EvidenceStatus;
  updatedAt: string;
  tags: string[];
}

interface Complaint {
  id: string;
  complainant: string;
  subject: string;
  receivedAt: string;
  dueAt: string;
  status: ComplaintStatus;
  linkedDomain: CqcDomain;
  action: string;
}

interface AuditTrailEvent {
  time: string;
  actor: string;
  event: string;
  relatedTo: string;
  outcome: string;
}

interface UkLegalRequirement {
  id: string;
  area: string;
  requirement: string;
  status: 'supported' | 'external_action' | 'needs_review';
  evidence: string;
  owner: string;
}

interface LegalDocumentLink {
  title: string;
  description: string;
  href: string;
}

const domainScores: DomainScore[] = [
  { domain: 'Safe', score: 82, rating: 'Good', evidenceCount: 18, gaps: 2 },
  { domain: 'Effective', score: 78, rating: 'Good', evidenceCount: 13, gaps: 3 },
  { domain: 'Caring', score: 86, rating: 'Good', evidenceCount: 11, gaps: 1 },
  { domain: 'Responsive', score: 74, rating: 'Requires improvement', evidenceCount: 10, gaps: 4 },
  { domain: 'Well-led', score: 80, rating: 'Good', evidenceCount: 16, gaps: 2 },
];

const evidenceItems: EvidenceItem[] = [
  {
    id: 'ev-001',
    title: 'Medication safety audit May 2026',
    domain: 'Safe',
    qualityStatement: 'Medicines optimisation',
    category: 'Processes',
    owner: 'Registered manager',
    status: 'current',
    updatedAt: '2026-05-20',
    tags: ['eMAR', 'PRN', 'MAR audit'],
  },
  {
    id: 'ev-002',
    title: 'Safeguarding concern escalation log',
    domain: 'Safe',
    qualityStatement: 'Safeguarding',
    category: 'Feedback from staff and leaders',
    owner: 'Safeguarding lead',
    status: 'current',
    updatedAt: '2026-05-18',
    tags: ['safeguarding', 'incident', 'training'],
  },
  {
    id: 'ev-003',
    title: 'Care plan review sample pack',
    domain: 'Effective',
    qualityStatement: 'Assessing needs',
    category: 'Processes',
    owner: 'Care coordinator',
    status: 'review_due',
    updatedAt: '2026-04-30',
    tags: ['care plans', 'reviews'],
  },
  {
    id: 'ev-004',
    title: 'Family feedback and communication records',
    domain: 'Caring',
    qualityStatement: 'Kindness, compassion and dignity',
    category: 'People experience',
    owner: 'Family liaison',
    status: 'current',
    updatedAt: '2026-05-19',
    tags: ['family portal', 'feedback'],
  },
  {
    id: 'ev-005',
    title: 'Complaints response improvement plan',
    domain: 'Responsive',
    qualityStatement: 'Listening to and involving people',
    category: 'Feedback from people',
    owner: 'Registered manager',
    status: 'gap',
    updatedAt: '2026-05-12',
    tags: ['complaints', 'Regulation 16'],
  },
  {
    id: 'ev-006',
    title: 'Governance meeting minutes',
    domain: 'Well-led',
    qualityStatement: 'Governance, management and sustainability',
    category: 'Processes',
    owner: 'Nominated individual',
    status: 'current',
    updatedAt: '2026-05-17',
    tags: ['governance', 'audit trail'],
  },
];

const initialComplaints: Complaint[] = [
  {
    id: 'cmp-001',
    complainant: 'Margaret Williams family',
    subject: 'Late evening visit communication',
    receivedAt: '2026-05-10',
    dueAt: '2026-05-24',
    status: 'investigating',
    linkedDomain: 'Responsive',
    action: 'Coordinator to confirm call procedure and update family communication note.',
  },
  {
    id: 'cmp-002',
    complainant: 'George Taylor',
    subject: 'Preference for same carer continuity',
    receivedAt: '2026-05-08',
    dueAt: '2026-05-22',
    status: 'resolved',
    linkedDomain: 'Caring',
    action: 'Preference logged in care plan and scheduling notes.',
  },
  {
    id: 'cmp-003',
    complainant: 'Anonymous staff concern',
    subject: 'Medication documentation consistency',
    receivedAt: '2026-05-12',
    dueAt: '2026-05-26',
    status: 'open',
    linkedDomain: 'Safe',
    action: 'Run medication competency refresher and review eMAR audit records.',
  },
];

const auditTrail: AuditTrailEvent[] = [
  {
    time: '2026-05-20 18:55',
    actor: 'Badan Catalin',
    event: 'PIR pack generated',
    relatedTo: 'CQC compliance',
    outcome: 'Local JSON evidence pack downloaded',
  },
  {
    time: '2026-05-20 18:44',
    actor: 'System',
    event: 'eMAR record saved',
    relatedTo: 'Medicines optimisation',
    outcome: 'MAR audit evidence tagged',
  },
  {
    time: '2026-05-20 18:21',
    actor: 'System',
    event: 'GP Connect readiness checked',
    relatedTo: 'Safe systems and pathways',
    outcome: 'Blocked pending NHS onboarding',
  },
  {
    time: '2026-05-19 16:10',
    actor: 'Registered manager',
    event: 'Complaint reviewed',
    relatedTo: 'Regulation 16',
    outcome: 'Action owner assigned',
  },
];

const pirSections = [
  'Service changes and registered manager details',
  'Safe care and treatment evidence',
  'Staffing, recruitment, training and supervision',
  'Medicines management and eMAR audit',
  'Complaints, safeguarding and learning culture',
  'People experience, family feedback and outcomes',
  'Governance, improvement plans and sustainability',
];

const ukLegalRequirements: UkLegalRequirement[] = [
  {
    id: 'uk-001',
    area: 'Software ownership',
    requirement: 'Project marked as private proprietary software owned by Dorobat Marius-Dorinel.',
    status: 'supported',
    evidence: 'LICENSE, OWNERSHIP.md and package metadata updated to UNLICENSED.',
    owner: 'Dorobat Marius-Dorinel',
  },
  {
    id: 'uk-002',
    area: 'Companies House',
    requirement: 'Company and PSC/beneficial ownership records must match the real operating business.',
    status: 'external_action',
    evidence: 'Software ownership register exists in repo; Companies House records must be maintained externally.',
    owner: 'Director / company secretary',
  },
  {
    id: 'uk-003',
    area: 'ICO / UK GDPR',
    requirement: 'Maintain lawful basis, privacy notices, retention schedule, DPIA and breach process.',
    status: 'needs_review',
    evidence: 'Access controls, role separation and server-side secret storage implemented; formal governance requires approval.',
    owner: 'Data protection lead',
  },
  {
    id: 'uk-004',
    area: 'CQC',
    requirement: 'Confirm regulated activity registration for personal care in England.',
    status: 'external_action',
    evidence: 'CQC evidence dashboard and PIR preparation pack implemented; official registration is external.',
    owner: 'Registered manager / nominated individual',
  },
  {
    id: 'uk-005',
    area: 'NHS DSP Toolkit',
    requirement: 'Complete DSPT before connecting to NHS systems or GP Connect.',
    status: 'external_action',
    evidence: 'GP Connect readiness blocks production NHS use until onboarding is complete.',
    owner: 'Information governance lead',
  },
  {
    id: 'uk-006',
    area: 'Security and secrets',
    requirement: 'Keep production secrets server-side and protect administrative access.',
    status: 'supported',
    evidence: 'API Keys panel saves through secured server endpoint; secrets are not returned to browser.',
    owner: 'Technical administrator',
  },
  {
    id: 'uk-007',
    area: 'Payroll',
    requirement: 'Validate carer pay calculations and payslip outputs with payroll/accounting process.',
    status: 'needs_review',
    evidence: 'Payroll page calculates from verified carer hours and generates PDF payslips.',
    owner: 'Payroll lead',
  },
  {
    id: 'uk-008',
    area: 'Clinical safety',
    requirement: 'Medication/eMAR workflows must be clinically reviewed before live medication decisions.',
    status: 'needs_review',
    evidence: 'eMAR readiness workflow exists; clinical safety case and hazard log require approval.',
    owner: 'Clinical safety lead',
  },
];

const legalDocumentLinks: LegalDocumentLink[] = [
  {
    title: 'Ownership, Licence and Copyright Declaration',
    description: 'Internal proprietary ownership, licence and copyright record for Dorobat Marius-Dorinel.',
    href: '/legal/01-ownership-rights-dorobat-marius-dorinel.pdf',
  },
];

const statusStyles: Record<EvidenceStatus, string> = {
  current: 'bg-emerald-100 text-emerald-800',
  review_due: 'bg-amber-100 text-amber-800',
  gap: 'bg-red-100 text-red-800',
};

const complaintStyles: Record<ComplaintStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  investigating: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
};

const ratingStyles: Record<DomainScore['rating'], string> = {
  Outstanding: 'bg-purple-100 text-purple-800',
  Good: 'bg-emerald-100 text-emerald-800',
  'Requires improvement': 'bg-amber-100 text-amber-800',
};

export const ComplianceDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'evidence' | 'pir' | 'complaints' | 'audit' | 'legal'>('overview');
  const [domainFilter, setDomainFilter] = useState<'All' | CqcDomain>('All');
  const [complaints, setComplaints] = useState(initialComplaints);

  const qScore = Math.round(
    domainScores.reduce((total, item) => total + item.score, 0) / domainScores.length
  );

  const filteredEvidence = domainFilter === 'All'
    ? evidenceItems
    : evidenceItems.filter((item) => item.domain === domainFilter);

  const openComplaints = complaints.filter((complaint) => complaint.status !== 'resolved').length;
  const evidenceGaps = evidenceItems.filter((item) => item.status === 'gap').length;
  const reviewDue = evidenceItems.filter((item) => item.status === 'review_due').length;
  const externalLegalActions = ukLegalRequirements.filter((item) => item.status !== 'supported').length;

  const pirPack = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    provider: 'FUCHSIA HOME CARE CATALIN',
    location: 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    qScore,
    domains: domainScores,
    evidence: evidenceItems,
    complaints,
    auditTrail,
    sections: pirSections,
    note: 'Local preparation pack. Submit only through the official CQC PIR link when requested.',
  }), [complaints, qScore]);

  const downloadPirPack = () => {
    const blob = new Blob([JSON.stringify(pirPack, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cqc-pir-preparation-pack-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('PIR preparation pack generated');
  };

  const markComplaintResolved = (id: string) => {
    setComplaints((items) =>
      items.map((item) =>
        item.id === id ? { ...item, status: 'resolved' } : item
      )
    );
    toast.success('Complaint marked as resolved');
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                CQC Compliance
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">CQC Advanced Hub</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Prepare evidence against CQC's five key questions, monitor a local Q-Score,
                track complaints under Regulation 16, build a PIR preparation pack and keep an
                audit trail for inspection readiness.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={downloadPirPack}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
          >
            <Download className="h-4 w-4" />
            Download PIR Pack
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-5">
        <MetricCard title="Q-Score" value={`${qScore}%`} subtitle="Local readiness score" icon={<BarChart3 className="h-5 w-5" />} />
        <MetricCard title="Evidence Gaps" value={String(evidenceGaps)} subtitle="Needs action" icon={<FolderSearch className="h-5 w-5" />} tone={evidenceGaps > 0 ? 'warning' : 'success'} />
        <MetricCard title="Review Due" value={String(reviewDue)} subtitle="Evidence needs update" icon={<FileCheck2 className="h-5 w-5" />} tone={reviewDue > 0 ? 'warning' : 'success'} />
        <MetricCard title="Open Complaints" value={String(openComplaints)} subtitle="Regulation 16 tracking" icon={<MessageSquareWarning className="h-5 w-5" />} tone={openComplaints > 0 ? 'warning' : 'success'} />
        <MetricCard title="UK Legal Actions" value={String(externalLegalActions)} subtitle="External review needed" icon={<ClipboardCheck className="h-5 w-5" />} tone={externalLegalActions > 0 ? 'warning' : 'success'} />
      </section>

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-amber-950">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This dashboard supports inspection preparation. It does not replace CQC registration,
            statutory notifications, PIR submission through CQC's official link, or management review
            of real policies, records and practice.
          </p>
        </div>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'overview', label: 'Q-Score' },
            { id: 'evidence', label: 'Evidence Tags' },
            { id: 'pir', label: 'PIR Reports' },
            { id: 'complaints', label: 'Complaints' },
            { id: 'audit', label: 'Audit Trail' },
            { id: 'legal', label: 'UK Legal' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <section className="grid gap-4 xl:grid-cols-5">
          {domainScores.map((item) => (
            <article key={item.domain} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{item.domain}</h2>
                  <p className="mt-1 text-sm text-slate-500">{item.evidenceCount} evidence items</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${ratingStyles[item.rating]}`}>
                  {item.rating}
                </span>
              </div>
              <div className="mt-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">Score</span>
                  <span className="font-semibold text-slate-950">{item.score}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-[#6c3a80]" style={{ width: `${item.score}%` }} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-slate-600">Gaps</span>
                <span className={item.gaps > 0 ? 'font-semibold text-amber-700' : 'font-semibold text-emerald-700'}>
                  {item.gaps}
                </span>
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'evidence' && (
        <section className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(['All', 'Safe', 'Effective', 'Caring', 'Responsive', 'Well-led'] as Array<'All' | CqcDomain>).map((domain) => (
              <button
                key={domain}
                type="button"
                onClick={() => setDomainFilter(domain)}
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                  domainFilter === domain
                    ? 'border-[#6c3a80] bg-[#6c3a80] text-white'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                {domain}
              </button>
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {filteredEvidence.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.domain}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.title}</h2>
                    <p className="mt-1 text-sm text-slate-600">{item.qualityStatement}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[item.status]}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <Detail label="Category" value={item.category} />
                  <Detail label="Owner" value={item.owner} />
                  <Detail label="Updated" value={item.updatedAt} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'pir' && (
        <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">PIR Preparation Pack</h2>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              CQC sends a unique PIR link to the registered manager. This export is a local
              preparation pack only, designed to organise answers and evidence before the official
              PIR is completed.
            </p>
            <button
              type="button"
              onClick={downloadPirPack}
              className="mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
            >
              <Download className="h-4 w-4" />
              Generate PIR JSON
            </button>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">PIR Sections Covered</h2>
            <div className="mt-4 grid gap-3">
              {pirSections.map((section) => (
                <div key={section} className="flex gap-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  {section}
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      {activeTab === 'complaints' && (
        <section className="space-y-4">
          {complaints.map((complaint) => (
            <article key={complaint.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-950">{complaint.subject}</h2>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${complaintStyles[complaint.status]}`}>
                      {complaint.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">Complainant: {complaint.complainant}</p>
                  <p className="mt-1 text-sm text-slate-600">Linked domain: {complaint.linkedDomain}</p>
                  <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">{complaint.action}</p>
                </div>
                <div className="min-w-52 space-y-2 text-sm text-slate-600">
                  <p>Received: {complaint.receivedAt}</p>
                  <p>Due: {complaint.dueAt}</p>
                  {complaint.status !== 'resolved' && (
                    <button
                      type="button"
                      onClick={() => markComplaintResolved(complaint.id)}
                      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Mark resolved
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'audit' && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <History className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Inspection Audit Trail</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Related to</th>
                  <th className="px-4 py-3">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {auditTrail.map((event) => (
                  <tr key={`${event.time}-${event.event}`}>
                    <td className="px-4 py-3 text-slate-600">{event.time}</td>
                    <td className="px-4 py-3 font-medium text-slate-950">{event.actor}</td>
                    <td className="px-4 py-3 text-slate-600">{event.event}</td>
                    <td className="px-4 py-3 text-slate-600">{event.relatedTo}</td>
                    <td className="px-4 py-3 text-slate-600">{event.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'legal' && (
        <section className="space-y-4">
          <article className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
            This checklist supports governance, but it does not replace official Companies House,
            ICO, CQC, NHS DSP Toolkit, payroll, clinical safety or solicitor review.
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#6c3a80]" />
                  <h2 className="text-xl font-bold text-slate-950">Ownership Document</h2>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  PDF record for proprietary ownership, licence and copyright evidence.
                </p>
              </div>
              <span className="rounded-md bg-[#6c3a80] px-3 py-2 text-sm font-semibold text-white">
                {legalDocumentLinks.length} PDFs
              </span>
            </div>

            <div className="mt-5 grid gap-3 lg:grid-cols-3">
              {legalDocumentLinks.map((document) => (
                <a
                  key={document.href}
                  href={document.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-h-32 flex-col justify-between rounded-lg border border-slate-200 bg-slate-50 p-4 transition hover:border-[#6c3a80] hover:bg-white hover:shadow-sm"
                >
                  <span>
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-semibold leading-5 text-slate-950">{document.title}</span>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-slate-400 group-hover:text-[#6c3a80]" />
                    </span>
                    <span className="mt-2 block text-sm leading-5 text-slate-600">{document.description}</span>
                  </span>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6c3a80]">
                    <Download className="h-4 w-4" />
                    Open PDF
                  </span>
                </a>
              ))}
            </div>
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {ukLegalRequirements.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.area}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.requirement}</h2>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      item.status === 'supported'
                        ? 'bg-emerald-100 text-emerald-800'
                        : item.status === 'external_action'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  {item.evidence}
                </p>
                <p className="mt-3 text-sm font-medium text-slate-600">Owner: {item.owner}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  tone = 'default',
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const colors = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${colors[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{title}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

export default ComplianceDashboard;
